import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Button } from "@interstate/components/Button";
import { PlusIcon } from "@interstate/components/Icons/PlusIcon";
import { MenuItem } from "./menu-item";

const PlusButtonContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const StyledButtonWrapper = styled.div<{ disabled: boolean }>`
  display: inline-flex;

  button {
    ${props =>
      props.disabled &&
      `
      opacity: 0.5;
      background-color: #f5f5f5 !important;
      cursor: not-allowed;
    `}
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  z-index: 10000;
  bottom: 50px;
  left: 0;
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  min-width: 150px;
  padding: 4px 0;
  overflow: hidden;
`;

export interface MenuItemConfig {
  /** Unique identifier for the menu item */
  id: string;
  /** Display label for the menu item */
  label: string;
  /** Click handler for the menu item */
  onClick: () => void;
  /** Optional test ID suffix for the menu item */
  testId?: string;
  /** When true, the item is excluded from the rendered menu */
  hidden?: boolean;
}

export interface PlusButtonProps {
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Test ID for testing purposes */
  "data-testid"?: string;
  /** Array of menu items to display */
  menuItems?: MenuItemConfig[];
}

/**
 * Plus Button component with dropdown menu
 * Displays a list of menu items that can be dynamically configured
 */
// eslint-disable-next-line react/no-multi-comp
export const PlusButton: React.FC<PlusButtonProps> = ({
  disabled = false,
  "data-testid": testId = "plus-button",
  menuItems = []
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const toggleMenu = useCallback(() => {
    if (!disabled) {
      setIsOpen(prev => !prev);
    }
  }, [disabled]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleMenuItemClick = useCallback(
    (item: MenuItemConfig) => {
      handleClose();
      item.onClick();
    },
    [handleClose]
  );

  // Close menu when clicking outside
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (
        buttonRef.current &&
        menuRef.current &&
        !buttonRef.current.contains(target) &&
        !menuRef.current.contains(target)
      ) {
        handleClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, handleClose]);

  const visibleItems = menuItems.filter(item => !item.hidden);

  // Don't render menu if no visible items
  if (visibleItems.length === 0) {
    return null;
  }

  return (
    <PlusButtonContainer data-testid={testId}>
      <StyledButtonWrapper disabled={disabled} title="Open menu">
        <Button
          aria-expanded={isOpen}
          aria-haspopup="menu"
          aria-label="Add options"
          buttonStyle="tertiary"
          data-testid={`${testId}-button`}
          disabled={disabled}
          ref={buttonRef}
          size="medium"
          onClick={toggleMenu}
        >
          <PlusIcon
            style={{
              transform: "scale(1.5)",
              color: disabled ? "#a0a0a0" : "#005BA8"
            }}
          />
        </Button>
      </StyledButtonWrapper>

      {isOpen && (
        <DropdownMenu data-testid={`${testId}-menu`} ref={menuRef} role="menu">
          {visibleItems.map(item => (
            <MenuItem
              data-testid={
                item.testId
                  ? `${testId}-${item.testId}`
                  : `${testId}-${item.id}`
              }
              key={item.id}
              onClick={() => handleMenuItemClick(item)}
            >
              {item.label}
            </MenuItem>
          ))}
        </DropdownMenu>
      )}
    </PlusButtonContainer>
  );
};
